import type { CityConfig } from "../city-template/types";

export const georgetownDeConfig: CityConfig = {
  city: "Georgetown",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "georgetown-de",
  route: "/cities/georgetown-de",
  title: "Emergency Passport Georgetown DE | Expedited Passport Sussex County Seat | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Georgetown, DE — as fast as 24 hours. Fast passport renewal for Sussex County seat residents and southern Delaware travelers. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Georgetown, DE",
  heroSubtitle: "Living in the Sussex County seat and need a passport fast? Fast Passport Center helps Georgetown, Millsboro, Long Neck, and southern Delaware residents get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20view%20of%20Georgetown%20Delaware%20historic%20downtown%20with%20Sussex%20County%20Courthouse%2C%20charming%20American%20small-town%20architecture%2C%20brick%20buildings%2C%20tree-lined%20streets%2C%20Delaware%20countryside%20with%20green%20fields%20and%20oak%20trees%2C%20warm%20golden%20afternoon%20sunlight%2C%20peaceful%20county%20seat%20atmosphere%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20landscape%20photography%20style&width=1800&height=700&seq=georgetown-de-hero-001&orientation=landscape",
  heroImageAlt: "Historic downtown Georgetown Delaware with Sussex County Courthouse",
  geoPosition: "38.6901;-75.3855",
  geoPlacename: "Georgetown, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "1111 N Independence Mall W, Philadelphia, PA 19106",
    distance: "Approximately 110–120 minutes from Georgetown, DE via US-113 N and I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Georgetown", zips: "19947" },
    { name: "Millsboro", zips: "19966" },
    { name: "Long Neck", zips: "19966" },
    { name: "Lewes", zips: "19958" },
    { name: "Rehoboth Beach", zips: "19971" },
    { name: "Bethany Beach", zips: "19930" },
    { name: "Dewey Beach", zips: "19971" },
    { name: "Ocean View", zips: "19970" },
    { name: "Selbyville", zips: "19975" },
    { name: "Seaford", zips: "19973" },
    { name: "Milford", zips: "19963" },
    { name: "Bridgeville", zips: "19933" },
    { name: "Ellendale", zips: "19941" },
    { name: "Greenwood", zips: "19950" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Georgetown and southern Sussex County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-home-5-line", title: "Sussex County Seat", desc: "Serving Georgetown, Millsboro, Long Neck, and southern Delaware coastal communities" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Rehoboth Beach, DE", route: "/cities/rehoboth-beach" },
    { name: "Emergency passport help in Seaford, DE", route: "/cities/seaford-de" },
    { name: "Urgent passport services in Milford, DE", route: "/cities/milford-de" },
    { name: "Delaware passport services overview", route: "/states/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Georgetown, Delaware remotely via secure registered courier. We do not have a walk-in office in Georgetown — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Georgetown and southern Sussex County travelers without the long drive to Philadelphia.",
  fpcOfficeDistance: "Serving Georgetown, Millsboro, Long Neck, and southern Sussex County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Georgetown, Delaware area and southern Sussex County. From the historic county seat to the coastal communities of Millsboro and Long Neck, from Lewes to Bethany Beach — we serve all of southern Delaware's communities including the popular beach resort areas.",
  nearbyCitiesIntro: "We serve Georgetown and nearby communities including Millsboro, Long Neck, Lewes, Rehoboth Beach, Bethany Beach, Dewey Beach, Ocean View, and Selbyville. We also serve residents in Seaford, Milford, and Bridgeville who prefer our expedited service over the self-service route.",
  howItWorksIntro: "Most Georgetown travelers complete everything online and ship documents overnight. No need to drive 110–120 minutes to Philadelphia or take time off from work.",
  mistakesIntro: "Georgetown, Delaware serves as the Sussex County seat with a unique mix of historic government workers, agricultural families, and coastal resort residents. Here are the most common passport mistakes we see from Georgetown travelers.",
  testimonialsIntro: "Georgetown, Delaware travelers — from county employees to beach resort residents — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Georgetown, DE? Here are the most common ones we hear from Sussex County travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 110–120 min to Philadelphia", "Full day commitment — challenging for families"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for family travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Georgetown travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute family trips and southern Delaware beach travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Georgetown adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Georgetown address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Georgetown residents and families — including first international trips.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Georgetown and southern Sussex County residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Georgetown families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Georgetown Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Georgetown DE", description: "Urgent passport processing for Georgetown Delaware travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Georgetown DE", description: "Fast passport renewal for Georgetown Delaware residents with a previously-issued passport." },
    { name: "First-Time Passport Georgetown DE", description: "New passport application services for Georgetown Delaware residents applying for their first passport." },
    { name: "Child Passport Georgetown DE", description: "Minor passport services for Georgetown Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Susan P.",
      location: "Georgetown, DE",
      rating: 5,
      text: "My daughter's passport expired two weeks before our trip to the Dominican Republic. Fast Passport Center had a new one in her hands in 4 days. As a working mom in Georgetown, I could not have driven to Philadelphia.",
      service: "Child Passport",
    },
    {
      name: "Robert E.",
      location: "Millsboro, DE",
      rating: 5,
      text: "Emergency business trip to Germany with three days notice. Fast Passport Center reviewed my documents, caught a photo issue, and delivered my renewed passport in 48 hours. Unbelievable for a Millsboro resident.",
      service: "Passport Renewal",
    },
    {
      name: "Barbara J.",
      location: "Lewes, DE",
      rating: 5,
      text: "Lost my passport before a cruise out of Baltimore. I live in Lewes and thought I would miss the boat. Fast Passport Center had a replacement to me in 3 days. I made the cruise with a day to spare.",
      service: "Lost Passport",
    },
    {
      name: "Charles M.",
      location: "Long Neck, DE",
      rating: 5,
      text: "First-time passport for my wife and me at 70 years old. We live in Long Neck and had never been out of the country. Fast Passport Center made the entire process easy and our passports arrived in 8 days.",
      service: "First-Time Passport",
    },
  ],
  facilities: [
    {
      id: "georgetown-de-1",
      name: "Georgetown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "115 N Race St",
      city: "Georgetown",
      state: "DE",
      zip: "19947",
      phone: "(302) 855-1231",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Georgetown, near Sussex County Courthouse",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Georgetown and southern Sussex County. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "georgetown-de-2",
      name: "Millsboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 Main St",
      city: "Millsboro",
      state: "DE",
      zip: "19966",
      phone: "(302) 934-8431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 10 miles east of Georgetown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for eastern Georgetown and Millsboro area residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "georgetown-de-3",
      name: "Lewes Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "126 Savannah Rd",
      city: "Lewes",
      state: "DE",
      zip: "19958",
      phone: "(302) 645-7714",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 18 miles east of Georgetown on the coast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for coastal Georgetown residents near Lewes and Rehoboth Beach. Call ahead to confirm availability.",
      icon: "ri-mail-line",
    },
    {
      id: "georgetown-de-4",
      name: "Seaford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2001 Bridgeville Hwy",
      city: "Seaford",
      state: "DE",
      zip: "19973",
      phone: "(302) 629-4591",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 18 miles west of Georgetown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for western Georgetown and Sussex County residents closer to the Seaford area.",
      icon: "ri-mail-line",
    },
    {
      id: "georgetown-de-5",
      name: "Milford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "301 NE Front St",
      city: "Milford",
      state: "DE",
      zip: "19963",
      phone: "(302) 422-6311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 20 miles north of Georgetown in Milford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility for Georgetown residents willing to travel north. More appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 110–120 min drive to Philadelphia", fpc: "No — ship from Georgetown" },
    { feature: "Family Travel Support", agency: "No flexibility for group needs", fpc: "Yes — we handle multi-passport cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Georgetown's beach-bound families often discover their passport needs just weeks before summer departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Georgetown and Sussex County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Knowing the Closest Acceptance Facility",
      description: "With Georgetown's location as the county seat, many residents from surrounding areas are unsure which post office is closest. The Georgetown Post Office at 115 N Race St is your primary local option.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Georgetown residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Georgetown or Millsboro." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Georgetown or Lewes." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Georgetown address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Georgetown, DE?", a: "Fast Passport Center offers emergency passport processing in Georgetown, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (110–120 minutes from Georgetown) — without you needing to make the drive." },
    { q: "Does Georgetown, Delaware have its own passport agency?", a: "No — neither Georgetown nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 110–120 minutes from Georgetown via US-113 and I-95. Fast Passport Center's courier service means Georgetown residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Millsboro or Long Neck?", a: "Absolutely. Fast Passport Center serves all of southern Sussex County including Millsboro, Long Neck, Lewes, Rehoboth Beach, and Bethany Beach. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Georgetown, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Georgetown, Delaware travelers use?", a: "Most Georgetown residents fly internationally through Philadelphia International Airport (PHL), approximately 110–120 minutes north via US-113 and I-95. Wilmington Airport (ILG) in New Castle offers limited domestic connections. Baltimore-Washington International (BWI) is an alternative for some travelers. Salisbury-Ocean City Wicomico Regional (SBY) in Maryland is nearby for regional travel. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Georgetown?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Georgetown, Millsboro, and Long Neck. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to Georgetown?", a: "The Georgetown Post Office at 115 N Race St is the primary local acceptance facility. The Millsboro Post Office at 100 Main St is nearby for eastern residents. The Lewes Post Office at 126 Savannah Rd is an option for coastal residents. All require appointments." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Georgetown residents should plan ahead, especially for summer beach trips and family vacations." },
  ],
  photoLocations: [
    { name: "Walgreens — Georgetown", address: "22016 DuPont Blvd, Georgetown, DE 19947", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Georgetown", address: "206 E Market St, Georgetown, DE 19947", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Georgetown", address: "206 E Market St, Georgetown, DE 19947", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Georgetown", address: "20930 DuPont Blvd, Georgetown, DE 19947", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    { name: "DMV — Georgetown", address: "420 N Bedford St, Georgetown, DE 19947", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves Georgetown and southern Sussex County. Appointments recommended.", featured: true },
    { name: "DMV — Dover", address: "3030 S Dupont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for northern Sussex County residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "110–120 miles from Georgetown, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Georgetown travelers. 110–120 minute drive north on US-113 and I-95. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "75 miles from Georgetown, DE",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to Georgetown with limited domestic service. No major international connections.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
    {
      code: "SBY",
      name: "Salisbury-Ocean City Wicomico Regional",
      distance: "35 miles from Georgetown, DE",
      address: "5485 Airport Terminal Rd, Salisbury, MD 21804",
      note: "Regional airport across the Maryland border with limited domestic connections. Useful for some regional travel.",
      flightUrl: "https://www.flysbyairport.com/",
      flightLabel: "SBY Airport",
      featured: false,
    },
  ],
};